import { Button } from "@scribe/ui/button";
import { Card, CardContent } from "@scribe/ui/card";
import BrandLogo from "@/components/brand-logo";
import { Checkbox } from "@/components/ui/checkbox";
import { getClientConsent, submitClientConsent } from "@/lib/apiSdk";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { CheckCircle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/consent/$token")({
  component: ClientConsentPage,
});

interface ConsentData {
  token: string;
  client_id: string;
  client_first_name: string;
  client_last_name: string;
  org_name: string;
  status: string;
  expires_at: number;
}

function ClientConsentPage() {
  const { token } = useParams({ from: "/consent/$token" });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ConsentData | null>(null);

  // Consent choices state
  const [recordingConsent, setRecordingConsent] = useState(true);
  const [aiConsent, setAiConsent] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    async function fetchConsentRequest() {
      try {
        setLoading(true);
        const res = await getClientConsent(token);
        setData(res as ConsentData);
        if (res.status !== "pending") {
          setError(
            res.status === "completed"
              ? "This consent request has already been completed."
              : "This consent request has expired or is no longer active."
          );
        }
      } catch {
        setError(
          "Invalid or expired consent link. Please ask your provider to send a new link."
        );
      } finally {
        setLoading(false);
      }
    }

    if (token) {
      fetchConsentRequest();
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setSubmitting(true);
    try {
      await submitClientConsent(token, recordingConsent, aiConsent);
      setSubmitted(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to submit consent choice."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="text-primary h-10 w-10 animate-spin" />
          <p className="text-muted-foreground text-sm font-medium">
            Loading secure form...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center p-8 text-center">
            <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
              {error}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted || data?.status === "completed") {
    const orgName = data?.org_name || "your provider";
    return (
      <div className="bg-background flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center p-8 text-center">
            <div className="bg-primary/10 text-primary mb-4 rounded-full p-3">
              <CheckCircle className="h-10 w-10" />
            </div>
            <h2 className="text-foreground text-xl font-bold">
              Consent Choices Saved
            </h2>
            <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
              Thank you. Your consent choices have been securey transmitted to
              your provider at <strong>{orgName}</strong>.
            </p>
            <p className="text-muted-foreground mt-2 text-xs">
              You can close this window now.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const clientName = `${data?.client_first_name} ${data?.client_last_name}`;

  return (
    <div className="bg-background flex min-h-screen items-center justify-center p-4 py-12">
      <Card className="w-full max-w-xl overflow-hidden">
        <div className="bg-muted/50 flex items-center justify-between border-b px-8 py-5">
          <div className="flex items-center gap-2.5">
            <BrandLogo className="size-6" />
            <span className="text-foreground text-md font-bold tracking-tight">
              Solu Health
            </span>
          </div>
          <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            Secure Consent
          </span>
        </div>

        <CardContent className="p-8">
          <h1 className="text-foreground text-2xl font-extrabold tracking-tight">
            Consent Form
          </h1>
          <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
            Please review the options below regarding session recording and AI
            note-taking for <strong>{clientName}</strong>, requested by{" "}
            <strong>{data?.org_name}</strong>.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            {/* Box 1: Session Audio Recording */}
            <div
              className={`hover:bg-accent/50 relative flex gap-4 rounded-xl border p-5 transition-colors select-none ${
                recordingConsent
                  ? "border-primary bg-muted/50"
                  : "border-border bg-card"
              }`}
            >
              <Checkbox
                id="recording-consent"
                checked={recordingConsent}
                onCheckedChange={(checked) => {
                  setRecordingConsent(checked === true);
                }}
                className="mt-1"
              />
              <label
                htmlFor="recording-consent"
                className="flex-1 cursor-pointer space-y-1"
              >
                <span className="text-foreground flex items-center gap-1.5 font-bold">
                  1. Audio Recording & Transcription
                </span>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  I consent to having my consultation sessions audio-recorded
                  and converted into written transcripts. All audio is
                  immediately deleted after transcription. This permits my
                  therapist to fully concentrate on our consultation instead of
                  writing notes manually.
                </p>
              </label>
            </div>

            {/* Box 2: AI Summarisation */}
            <div
              className={`hover:bg-accent/50 relative flex gap-4 rounded-xl border p-5 transition-colors select-none ${
                aiConsent
                  ? "border-primary bg-muted/50"
                  : "border-border bg-card"
              }`}
            >
              <Checkbox
                id="ai-consent"
                checked={aiConsent}
                onCheckedChange={(checked) => setAiConsent(checked === true)}
                className="mt-1"
              />
              <label
                htmlFor="ai-consent"
                className="flex-1 cursor-pointer space-y-1"
              >
                <span className="text-foreground flex items-center gap-1.5 font-bold">
                  2. AI-Assisted Summarization
                </span>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  I consent to the securely transcribed text being processed by
                  AI (artificial intelligence) to draft clinical notes.
                  AI-generated notes are only accessed by my therapist, who
                  reviews and approves them.
                </p>
              </label>
            </div>

            <div className="border-border/40 mt-8 border-t pt-6">
              <Button
                type="submit"
                disabled={submitting}
                className="text-md w-full py-6 font-bold"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting Choices…
                  </>
                ) : (
                  "Confirm and Submit Choices"
                )}
              </Button>
              <p className="text-muted-foreground mt-4 text-center text-xs">
                To learn more about how your data is handled, read our{" "}
                <a
                  href="/consent/info"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80 font-medium underline"
                >
                  Clinical Audio & AI Disclosure
                </a>
                .
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
